// Number formatting utilities

/**
 * Format a number as currency with commas and 2 decimal places
 * @param {number} amount - The number to format
 * @param {string} locale - Locale string (default: 'en-US')
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale = 'en-PH', currency = 'PHP') => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0.00';
    }
  
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  
  /**
   * Format a number with commas and specified decimal places
   * @param {number} number - The number to format
   * @param {number} decimals - Number of decimal places (default: 2)
   * @param {string} locale - Locale string (default: 'en-US')
   * @returns {string} Formatted number string
   */
  export const formatNumber = (number, decimals = 2, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) {
      return `0.${'0'.repeat(decimals)}`;
    }
  
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  };
  
  /**
   * Format a number with commas (no decimal places)
   * @param {number} number - The number to format
   * @param {string} locale - Locale string (default: 'en-US')
   * @returns {string} Formatted number string
   */
  export const formatNumberNoDecimals = (number, locale = 'en-US') => {
    if (number === null || number === undefined || isNaN(number)) {
      return '0';
    }
  
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(number);
  };
  
  /**
   * Parse a formatted number string back to a number
   * @param {string} formattedString - The formatted number string
   * @returns {number} Parsed number
   */
  export const parseFormattedNumber = (formattedString) => {
    if (!formattedString) return 0;
    
    // Remove all non-numeric characters except decimal point
    const numericString = formattedString.replace(/[^\d.-]/g, '');
    return parseFloat(numericString) || 0;
  };
  
  /**
   * Format percentage with specified decimal places
   * @param {number} number - The number to format as percentage
   * @param {number} decimals - Number of decimal places (default: 1)
   * @returns {string} Formatted percentage string
   */
  export const formatPercentage = (number, decimals = 1) => {
    if (number === null || number === undefined || isNaN(number)) {
      return `0.${'0'.repeat(decimals)}%`;
    }
  
    return `${number.toFixed(decimals)}%`;
  };
  
  /**
   * Abbreviate large numbers (K, M, B, T)
   * @param {number} number - The number to abbreviate
   * @param {number} decimals - Number of decimal places (default: 1)
   * @returns {string} Abbreviated number string
   */
  export const abbreviateNumber = (number, decimals = 1) => {
    if (number === null || number === undefined || isNaN(number)) {
      return '0';
    }
  
    const absNumber = Math.abs(number);
    const sign = number < 0 ? '-' : '';
  
    if (absNumber >= 1e12) {
      return sign + (absNumber / 1e12).toFixed(decimals) + 'T';
    } else if (absNumber >= 1e9) {
      return sign + (absNumber / 1e9).toFixed(decimals) + 'B';
    } else if (absNumber >= 1e6) {
      return sign + (absNumber / 1e6).toFixed(decimals) + 'M';
    } else if (absNumber >= 1e3) {
      return sign + (absNumber / 1e3).toFixed(decimals) + 'K';
    } else {
      return sign + absNumber.toFixed(decimals);
    }
  };
  
  /**
   * Validate if a string is a valid number
   * @param {string} value - The string to validate
   * @returns {boolean} True if valid number
   */
  export const isValidNumber = (value) => {
    if (value === null || value === undefined || value === '') return false;
    return !isNaN(parseFloat(value)) && isFinite(value);
  };
  
  /**
   * Round to specified decimal places
   * @param {number} number - The number to round
   * @param {number} decimals - Number of decimal places (default: 2)
   * @returns {number} Rounded number
   */
  export const roundToDecimal = (number, decimals = 2) => {
    if (number === null || number === undefined || isNaN(number)) {
      return 0;
    }
    
    const factor = Math.pow(10, decimals);
    return Math.round((number + Number.EPSILON) * factor) / factor;
  };
  
  // Default export with all utilities
  const numberUtils = {
    formatCurrency,
    formatNumber,
    formatNumberNoDecimals,
    parseFormattedNumber,
    formatPercentage,
    abbreviateNumber,
    isValidNumber,
    roundToDecimal,
  };
  
  export default numberUtils;